import { isRideTooSoon } from "../bookings/booking-time";

/**
 * "Need a ride there?" after a booking: which bookings get the offer, the
 * pick-up it suggests, and when it is too late to offer. Shared by the
 * website's thank-you page and the app's booking confirmation so the two
 * never disagree about who is offered a driver.
 */

/** The booking, as far as the ride offer needs to know it. */
export interface RideThereBooking {
  kind: "transfer" | "restaurant" | "nightclub" | "stay" | "experience";
  citySlug: string | null;
  /** YYYY-MM-DD; a longer ISO string is cut to the day. */
  date: string;
  /** HH:MM, when the booking has one. */
  time: string | null;
}

export type RideTile = "restaurant" | "night" | "agafay";

/** Which ride a booking is offered, or null when it gets no offer. */
export function rideTileFor(p: Pick<RideThereBooking, "kind" | "citySlug">): RideTile | null {
  if (p.kind === "transfer") return null;
  // Transfers run from Marrakech, so a Casablanca table gets no card.
  if (p.citySlug === "agafay") return "agafay";
  if (p.citySlug !== "marrakech") return null;
  if (p.kind === "restaurant") return "restaurant";
  if (p.kind === "nightclub") return "night";
  return null;
}

/** The transfer route (an activity slug) each ride books. */
export const RIDE_ROUTE_SLUG: Record<RideTile, string> = {
  restaurant: "marrakech-dinner-transfer",
  night: "marrakech-nightclub-transfer",
  agafay: "marrakech-agafay-transfer",
};

/**
 * The pick-up the card suggests: before the table or the club by 30 minutes,
 * before an Agafay evening by an hour (about 45 minutes by road). A time that
 * would fall the day before is left out; the guest picks it on the form.
 */
export function suggestedPickup(time: string | null, tile: RideTile): string | null {
  const m = time && /^(\d{2}):(\d{2})/.exec(time);
  if (!m) return null;
  const at = Number(m[1]) * 60 + Number(m[2]) - (tile === "agafay" ? 60 : 30);
  if (at < 0) return null;
  const snapped = Math.floor(at / 30) * 30;
  return `${String(Math.floor(snapped / 60)).padStart(2, "0")}:${String(snapped % 60).padStart(2, "0")}`;
}

/**
 * Too late to offer the ride: rides need two hours' notice (owner,
 * 2026-09-30). Judged on the suggested pick-up; with none (no time, or a
 * pick-up that would fall the day before), on the booking day, which is too
 * late once no pick-up left on it is two hours away. Also covers a past day.
 */
export function rideThereTooSoon(p: RideThereBooking, now: Date = new Date()): boolean {
  const tile = rideTileFor(p);
  const date = p.date.slice(0, 10);
  const pickup = tile ? suggestedPickup(p.time, tile) : null;
  return isRideTooSoon({ date, time: pickup }, now);
}
